import {
  BOX_CATEGORY_INFO,
  type BoxCategory,
  boxCategoryFromSlug,
  boxCategorySlug,
} from "@loot-os/api-contract";
import type { BoxCategoriesContent } from "@loot-os/design-system/templates/boxes/boxesContent";
import { useQuery } from "@tanstack/react-query";
import { boxCategoriesOf } from "../../../../lib/boxCategories.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useCatalogCopy } from "./copy.js";
import { useBoxesSearch } from "./search.js";

/** The row's categories in list order, and the one showing (null = every box). */
interface BoxCategoryRow {
  shown: BoxCategory[];
  active: BoxCategory | null;
}

/**
 * The category row, or null when there is none to show. It offers each category the store
 * switched on that a listed box is in (while the boxes load, every one it switched on), and the
 * one the URL names even when nothing is in it. A lone category with nothing beneath it to fan out
 * to is no row at all, since All and it would show nearly the same grid.
 */
function useBoxCategoryRow(): BoxCategoryRow | null {
  const { config } = useTenant();
  const [search] = useBoxesSearch();
  const boxes = useQuery(boxesQueryOptions()).data;

  const offered = boxCategoriesOf(config);
  const requested = boxCategoryFromSlug(search.category);
  const active = requested && offered.includes(requested) ? requested : null;
  const listed = boxes ? new Set(boxes.flatMap((box) => box.categories ?? [])) : null;
  const shown = offered.filter(
    (category) => !listed || listed.has(category) || category === active,
  );
  // A family whose games are switched on brings them into the row, so one entry fans out to none.
  if (shown.length < 2) return null;
  return { shown, active };
}

/**
 * The box category the grid is narrowed to: the URL's, while the store offers it and shows the
 * row. A link to a category the store switched off, or to one while the row is hidden, shows
 * every box, so a shopper never lands on a narrowed grid with no chip to widen it.
 */
export function useActiveBoxCategory(): BoxCategory | null {
  return useBoxCategoryRow()?.active ?? null;
}

/**
 * The category chips: every box, then the row's categories. Left out with the row, so a store that
 * offers none, or only one with nothing to fan out to, shows no chips. Like the tab, the chip
 * showing lives in the URL (`?category=`) and Clear leaves it.
 */
export function useBoxCategoriesContent(): BoxCategoriesContent | undefined {
  const copy = useCatalogCopy().categories;
  const [, update] = useBoxesSearch();
  const row = useBoxCategoryRow();
  if (!row) return undefined;
  return {
    value: row.active ? boxCategorySlug(row.active) : "all",
    label: copy.label,
    options: [
      { value: "all", label: copy.all },
      ...row.shown.map((category) => ({
        value: boxCategorySlug(category),
        label: BOX_CATEGORY_INFO[category].label,
      })),
    ],
    onValueChange: (value) => update({ category: value === "all" ? undefined : value }),
  };
}
